/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import Axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import {state} from '@/store/modules/user-session';
import router from '@/router';
import ApiException from '@/exceptions/ApiException';


/**
 * Api service class used to leverage ajax library in order to communicate with api.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
class ApiService {
    /**
     * Apply authorisation token to request if available.
     *
     * @param {AxiosRequestConfig} config
     *
     * @returns {AxiosRequestConfig}
     */
    private static applyAuthorisationToken(config: AxiosRequestConfig): AxiosRequestConfig {
        // @todo is there a built in skip authorisation?
        if (!config.params.skipAuthorization &&
            '' !== state.session.token &&
            state.session.expiry > (new Date().getDate())) {
            config.headers.Authorization = `Bearer ${state.session.token}`;
        }

        // clear non-standard params
        if (config.params.skipAuthorization) {
            config.params.skipAuthorization = undefined;
        }

        return config;
    }

    /**
     * Apply Content Type Application/JSON to all requests
     *
     * @param {AxiosRequestConfig} config
     *
     * @returns {AxiosRequestConfig}
     */
    private static applyContentType(config: AxiosRequestConfig): AxiosRequestConfig {
        if (!config.params.noContentTypeHeader) {
            config.headers['content-type'] = 'application/json';
        }

        return config;
    }

    /**
     * Global success response handler
     * @param {AxiosResponse} response
     * @returns {AxiosResponse}
     */
    private static handleSuccess(response: AxiosResponse): AxiosResponse {
        // Custom response handling
        return response;
    }

    /**
     * Global error response handler
     * @param error
     * @returns {Promise<any>}
     */
    private static handleError(error: AxiosError): Promise<never> {
        if (!error.response) {
            return Promise.reject(error);
        }

        const exception: ApiException = new ApiException(
            error.response.status,
            error.response.data,
        );

        // Error
        switch (exception.status) {
            case 401:
                // if we are currently on the login page then ignore
                if ('login' !== router.currentRoute.name) {
                    router.push('/login');
                }
                break;
            case 412:
                // if we are currently on the terms page then ignore
                if ('terms' !== router.currentRoute.name) {
                    router.push('/eula');
                }

                break;
        }

        // Custom error handling
        return Promise.reject(exception);
    }

    /**
     * Axios ajax service instance.
     *
     * @var AxiosInstance
     */
    public axios: AxiosInstance;

    /**
     *  Constructor.
     *
     *  Configuration of the axios request and response interceptors.
     */
    constructor() {
        this.axios = Axios.create({
            baseURL: process.env.VUE_APP_API,
            params: {
                // mode: 'no-cors',
                // _format: 'hal_json',
            },
        });

        // add response interceptor
        this.axios.interceptors.response.use(ApiService.handleSuccess, ApiService.handleError);

        // add request interceptor
        this.axios.interceptors.request.use(ApiService.applyAuthorisationToken);
        this.axios.interceptors.request.use(ApiService.applyContentType);
    }
}


export default new ApiService();
